import React, { PureComponent } from 'react'
import { View, Platform } from 'react-native'
import styled from 'styled-components/native'
import theme from '../../../../styles/theme'
import { Text } from '../../../Atoms/Typography'
import { translate } from '../../../../services/translate'
import { StackedBarChart } from 'react-native-svg-charts'
import { Text as SvgText, G, Rect, TSpan } from 'react-native-svg'

const ContainerRow = styled(View)`
  flex: 1;
  flex-direction: row;
  padding: ${theme.gutterUnit * 2}px;
  background: ${theme.colours.white};
`

const Container = styled(View)`
  flex-grow: 1;
  background: ${theme.colours.white};
`

const LegendWrapper = styled(View)`
  flex-direction: row;
  justify-content: center;
`
const LegendItem = styled(View)`
  flex-direction: row;
  padding: ${theme.gutterUnit * 1.5}px ${theme.gutterUnit}px 0 ${theme.gutterUnit}px;
`
const LegendItemGain = styled(View)`
  width: 20px;
  height: 20px;
  margin-right: 10px;
  background: ${theme.colours.green};
`
const LegendItemLoss = styled(View)`
  width: 20px;
  height: 20px;
  margin-right: 10px;
  background: #edd4d7;
`

const BandWidthFactor = 0.2
const yAxisTickSpacing = 500
export default class ReturnOnEquity extends PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      isMeasurable: false,
      width: 0,
      height: 0
    }
  }

  handleLayout = event => {
    if (this.state.isMeasurable) return // layout was already called
    const { width, height } = event.nativeEvent.layout
    this.setState({
      isMeasurable: true,
      width: width - theme.gutterUnit * 4,
      height: height - theme.gutterUnit * 4
    })
  }

  getChart (data, currentTeam, selectedRound) {
    return {
      chartProps: {
        style: {
          width: this.state.width,
          height: this.state.height
        },
        data: data.map(({ team, name, value, last }) => {
          const data = {
            name,
            team,
            value: {
              value: selectedRound === 1 ? value : value > last ? last : value,
              svg: {
                fill: team === currentTeam ? theme.colours.chart.orange : theme.colours.chart.blue
              }
            },
            diff: {
              value: selectedRound === 1 ? 0 : value > last ? value - last : last - value,
              gain: value > last,
              svg: {
                fill: value > last ? theme.colours.green : '#edd4d7'
              }
            }
          }
          if (value < last) {
            data.diff.svg.stroke = '#bb4850'
            data.diff.svg.strokeDasharray = [10, 10]
          }
          return data
        }),
        keys: ['value', 'diff'],
        colors: [theme.colours.chart.blue, theme.colours.chart.blue],
        valueAccessor: ({ item, key }) => item[key].value,
        spacingInner: BandWidthFactor,
        gridMin: 0,
        gridMax: Math.ceil(Math.max.apply(Math, data.reduce((acc, { value, last }) => {
          acc.push(value)
          if (last) acc.push(last)
          return acc
        }, [])) / yAxisTickSpacing) * yAxisTickSpacing,
        contentInset: {
          top: 10,
          bottom: 30,
          left: 40,
          right: 10
        }
      }
    }
  }

  render () {
    const { chartProps } = this.getChart(this.props.data, this.props.currentTeam, this.props.selectedRound)
    const Labels = ({ x, y, data, width }) => (
      data.map(({ value, diff }, index) => {
        const yOffset = Platform.OS === 'web' ? 16 : 6
        return (
          <SvgText
            key={index}
            x={x(index) + (width / data.length * (1 - BandWidthFactor)) / 2}
            y={y(diff.gain ? value.value + diff.value : value.value) + yOffset}
            fontSize={14}
            fontWeight='bold'
            fontFamily={Platform.select({ web: 'ubuntu-bold' })} // TODO fontFamily fix
            fill='#000'
            alignmentBaseline='top'
            textAnchor='middle'
          >
            {parseFloat((diff.gain ? value.value + diff.value : value.value) / 100).toFixed(2) + '%'}
          </SvgText>
        )
      })
    )
    const TeamNames = ({ x, y, data, width }) => (
      data.map(({ name }, index) => {
        const yOffset = Platform.OS === 'web' ? 16 : 10
        return (
          <SvgText
            key={index}
            x={x(index) + (width / data.length * (1 - BandWidthFactor)) / 2}
            y={y(0) + yOffset}
            fontSize={14}
            fontWeight='bold'
            fontFamily={Platform.select({ web: 'ubuntu-medium' })} // TODO fontFamily fix
            fill='#888888'
            alignmentBaseline='top'
            textAnchor='middle'
          >
            <TSpan>{name.replace('Bank', '').substring(0, 8)}</TSpan>
          </SvgText>
        )
      })
    )
    const YAxis = ({ x, y, data, width }) => {
      const items = []
      for (let value = chartProps.gridMin; value <= chartProps.gridMax; value += yAxisTickSpacing) {
        items.push(
          <G
            key={value}
            style={{ zIndex: 0 }}
          >
            <Rect
              x={x(0)}
              y={y(value)}
              width={width - x(0)}
              height={1}
              fill='#eeeeee'
            />
            <SvgText
              x={x(0) - 10}
              y={y(value)}
              fontFamily={Platform.select({ web: 'ubuntu-bold' })} // TODO fontFamily fix
              fontSize={14}
              fontWeight='bold'
              alignmentBaseline='middle'
              textAnchor='end'
              fill='#888888'
            >
              {value / 100 + '%'}
            </SvgText>
          </G>
        )
      }
      return items
    }
    return (
      <ContainerRow>
        <Container onLayout={this.handleLayout}>
          {this.state.isMeasurable &&
            <StackedBarChart {...chartProps}>
              <Labels />
              <TeamNames />
              <YAxis belowChart />
            </StackedBarChart>}
          <LegendWrapper>
            <LegendItem>
              <LegendItemGain />
              <Text>{`ROE ${translate('peerInsights.screen1.gain', 'Increase')}`}</Text>
            </LegendItem>
            <LegendItem>
              <LegendItemLoss />
              <Text>{`ROE ${translate('peerInsights.screen1.loss', 'Reduction')}`}</Text>
            </LegendItem>
          </LegendWrapper>
        </Container>
      </ContainerRow>
    )
  }
}
